import html from "nanohtml";
import type { DetailedWidgetUIProps } from "../../types.ts";
import { GoDeeperArticle } from "../components/goDeeperArticle.ts";
import { AdUnit } from "../components/adUnit.ts";

export type GoDeeperWidgetProps = DetailedWidgetUIProps & {
  adLocation?: "in-article" | "bottom-of-article";
};

export function GoDeeperWidget(props: GoDeeperWidgetProps): HTMLElement {
  const adLocation = props.adLocation || "in-article";
  // Create articles with ads based on adLocation
  const contentItems: HTMLElement[] = [];

  props.posts.forEach((post, index) => {
    // Create ad unit for this article
    const adUnit = AdUnit({
      unitNumber: index + 1,
      logger: props.logger,
      continueText:
        adLocation === "bottom-of-article"
          ? "More content follows below the ad"
          : undefined,
    });

    // Add article with conditional ad placement
    const articleElement = GoDeeperArticle({
      ...post,
      logger: props.logger,
      adUnit: adLocation === "in-article" ? adUnit : undefined,
    });

    // Add unique ID for intersection observer tracking
    articleElement.setAttribute("data-post-id", post.id);
    articleElement.setAttribute("data-post-order", (index + 1).toString());

    contentItems.push(articleElement);

    // Add ad between articles if adLocation is bottom-of-article
    if (adLocation === "bottom-of-article") {
      contentItems.push(
        html`<div
          class="rw-mt-12 rw-border-0 rw-border-t rw-border-solid rw-border-gray-800"
        >
          ${adUnit}
        </div> `
      );
    }
  });

  const widget = html`
    <div
      class="rw-widget rw-my-4 renderer-${props.siteRenderer}"
      role="region"
      aria-label="Related articles"
    >
      <div role="main" aria-label="Related articles">${contentItems}</div>
      <div class="rw-sr-only" aria-live="polite">
        ${props.posts.length} articles loaded with ${props.posts.length}
        advertisements
        ${adLocation === "in-article"
          ? "embedded within articles"
          : "between articles"}
      </div>
    </div>
  `;

  // Set up intersection observer for viewable events after widget is rendered
  setTimeout(() => {
    setupViewableTracking(widget, props.logger);
  }, 100);

  return widget;
}

/**
 * Sets up intersection observer to track when posts become 20% visible
 */
function setupViewableTracking(
  widget: HTMLElement,
  logger: (event: any) => void
): void {
  const articles = widget.querySelectorAll("[data-post-id]");
  const viewedPosts = new Set<string>(); // Track which posts have already fired viewable events

  if (!articles.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
          const element = entry.target as HTMLElement;
          const postId = element.getAttribute("data-post-id");
          const postOrder = element.getAttribute("data-post-order");

          if (postId && postOrder && !viewedPosts.has(postId)) {
            viewedPosts.add(postId);

            logger({
              name: "post_viewable",
              articleId: postId,
              postOrder: parseInt(postOrder, 10),
            });

            // Stop observing this element once it's been viewed
            observer.unobserve(entry.target);
          }
        }
      });
    },
    {
      threshold: 0.2, // Trigger when 20% visible
      rootMargin: "0px",
    }
  );

  // Start observing all article elements
  articles.forEach((article) => {
    observer.observe(article);
  });
}
